import { useCallback, useEffect, useState } from 'react';
import {
  Award, Banknote, CheckCircle2, ClipboardList, Download, FileText,
  Loader2, Send, Upload, Wallet, X,
} from 'lucide-react';
import { adminApi } from '../services/api';
import Fenetre from './Fenetre';
import {
  annoncerErreur, annoncerSucces, Bandeau, demanderMotif, EtatErreur, SqueletteCarte, SqueletteTableau, SqueletteTexte, validerFichier,
} from '../etats';
import EtatVide from './EtatVide';
import { BoutonSigner } from './SignerDocument';
import MesRetraits from './MesRetraits';

/* ═══════════════════════════════════════════════════════════════════════
   MES DOCUMENTS — l'espace de l'intervenant

   Trois choses que cet écran doit faire comprendre, et qu'un intervenant
   découvrirait autrement à ses dépens :

   · **La fiche naît de la séance, pré-remplie.** On ne saisit que ce que
     soi seul peut dire — objectifs, points traités, prochaines étapes,
     présence. Le reste, la plateforme le sait déjà.

   · **Une fiche non reçue n'est pas facturable.** Tant qu'elle attend
     chez le point focal, elle ne peut pas porter sur le relevé du mois.
     La préparation le montre avant d'émettre.

   · **Un bénévole n'émet pas de relevé**, et ce n'est pas un bouton
     grisé : c'est une phrase, parce qu'une facture émise sous convention
     de bénévolat ferait requalifier l'engagement en rémunération.
   ═══════════════════════════════════════════════════════════════════════ */

const ETATS = {
  brouillon: { libelle: 'Brouillon', couleur: 'var(--text-muted)' },
  a_signer: { libelle: 'À signer', couleur: 'var(--attention)' },
  transmise: { libelle: 'Transmise', couleur: 'var(--attention)' },
  recue: { libelle: 'Reçue', couleur: 'var(--succes)' },
  a_corriger: { libelle: 'À corriger', couleur: 'var(--alerte)' },
  emis: { libelle: 'Émis', couleur: 'var(--text-muted)' },
  transmis: { libelle: 'Transmis', couleur: 'var(--attention)' },
  valide: { libelle: 'Validé', couleur: 'var(--succes)' },
  paye: { libelle: 'Payé', couleur: 'var(--succes)' },
  annule: { libelle: 'Annulé', couleur: 'var(--alerte)' },
};

function Etiquette({ etat }) {
  const { libelle, couleur } = ETATS[etat] || { libelle: etat, couleur: 'var(--text-muted)' };
  return <span className="text-[12px] font-semibold" style={{ color: couleur }}>{libelle}</span>;
}

const jour = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};
const francs = (v) => `${Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} FCFA`;
/* Le serveur rend « 7.50 » — un décimal JSON, pas un texte français. Le
   laisser tel quel imprime « 7.50 % » sur un document de facturation. */
const pourcent = (v) => Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

/* Les scans signés : un PDF ou une photo, vérifiés avant l'envoi. */
const FORMATS_SCAN = ['.pdf', '.png', '.jpg', '.jpeg'];

/* Une actualisation qui échoue ne retire pas ce qui est affiché : elle se
   signale au-dessus, avec de quoi réessayer. */
function AvisActualisation({ erreur, onReessayer }) {
  if (!erreur) return null;
  return (
    <Bandeau ton="attention" action={<button type="button" className="act act-sm act-contour" onClick={onReessayer}>Réessayer</button>}>
      L’actualisation a échoué : ces données sont peut-être anciennes.
    </Bandeau>
  );
}

/* ── Le formulaire d'une fiche ────────────────────────────────────── */

const CHAMPS = [
  ['objectifs', 'Objectifs de la séance',
   "Ce que la séance devait permettre d'obtenir."],
  ['points_traites', 'Points traités et recommandations',
   'Ce qui a été examiné, et ce que vous avez recommandé.'],
  ['prochaines_etapes', 'Prochaines étapes convenues',
   'Ce que le bénéficiaire fera d’ici la prochaine séance.'],
];

const PRESENCES = [
  ['present', 'Présent'],
  ['excuse', 'Excusé'],
  ['absent', 'Absent'],
];

/* Exportée : l'onglet Séances l'ouvre en fenêtre dès qu'une séance est
   notée réalisée — le rapport se remplit pendant que la séance est fraîche. */
export function Fiche({ fiche, onFerme, onChange, signaturePrete }) {
  const [form, setForm] = useState({
    objectifs: fiche.objectifs || '',
    points_traites: fiche.points_traites || '',
    prochaines_etapes: fiche.prochaines_etapes || '',
    presence: fiche.presence || 'present',
    observations: fiche.observations || '',
  });
  const [enCours, setEnCours] = useState('');
  const [erreur, setErreur] = useState('');

  const modifiable = ['brouillon', 'a_corriger'].includes(fiche.statut);

  const agir = async (quoi, action) => {
    setEnCours(quoi); setErreur('');
    try {
      const resultat = await action();
      onChange(resultat);
      return resultat;
    } catch (e) {
      setErreur(e.message);
      return null;
    } finally {
      setEnCours('');
    }
  };

  const deposerScan = async (evenement) => {
    const fichier = evenement.target.files?.[0];
    if (!fichier) return;
    evenement.target.value = '';
    const probleme = validerFichier(fichier, { extensions: FORMATS_SCAN });
    if (probleme) { setErreur(probleme); return; }
    const corps = new FormData();
    corps.append('fichier', fichier);
    const ok = await agir('scan', () => adminApi.deposerScanFiche(fiche.id, corps));
    if (ok) annoncerSucces('Scan signé déposé.');
  };

  return (
    <div className="rounded-carte bg-[var(--bg-card)] shadow-carte p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="titre-carte">
            {fiche.numero || 'Fiche en brouillon'}
            {fiche.version > 1 && (
              <span className="ml-2 text-[12px] font-medium text-[var(--text-muted)]">
                version {fiche.version}
              </span>
            )}
          </h3>
          <p className="text-[12.5px] text-[var(--text-muted)] mt-1">
            {fiche.organisation} — séance du {jour(fiche.date_seance)} · <Etiquette etat={fiche.statut} />
          </p>
        </div>
        <button type="button" onClick={onFerme} className="act act-tertiaire" title="Fermer">
          <X className="w-4 h-4" />
        </button>
      </div>

      {fiche.motif_correction && (
        <div className="encart encart-alerte">
          <strong>À corriger :</strong> {fiche.motif_correction}
        </div>
      )}
      {erreur && <div role="alert" className="encart encart-alerte">{erreur}</div>}

      {CHAMPS.map(([cle, libelle, aide]) => (
        <div key={cle}>
          <label className="block surtitre text-[var(--text-muted)] mb-1.5" htmlFor={`f-${cle}`}>
            {libelle}
          </label>
          <textarea
            id={`f-${cle}`}
            value={form[cle]}
            onChange={(e) => setForm({ ...form, [cle]: e.target.value })}
            disabled={!modifiable}
            rows={cle === 'points_traites' ? 5 : 3}
            className="champ resize-none"
          />
          <p className="text-[11.5px] text-[var(--text-muted)] mt-1">{aide}</p>
        </div>
      ))}

      <div>
        <span className="block surtitre text-[var(--text-muted)] mb-1.5">
          Présence du bénéficiaire
        </span>
        {/* Elle décide de la facturation : une séance où le bénéficiaire
            était absent atteste justement qu'il ne s'est rien passé. */}
        <div className="flex gap-4 flex-wrap">
          {PRESENCES.map(([cle, libelle]) => (
            <label key={cle} className="inline-flex items-center gap-1.5 text-[13.5px]">
              <input type="radio" name="presence" value={cle}
                     checked={form.presence === cle}
                     disabled={!modifiable}
                     onChange={() => setForm({ ...form, presence: cle })} />
              {libelle}
            </label>
          ))}
        </div>
        <p className="text-[11.5px] text-[var(--text-muted)] mt-1">
          Seule une séance suivie est facturable.
        </p>
      </div>

      <div>
        <label className="block surtitre text-[var(--text-muted)] mb-1.5" htmlFor="f-observations">
          Observations
        </label>
        <textarea id="f-observations" value={form.observations}
                  onChange={(e) => setForm({ ...form, observations: e.target.value })}
                  disabled={!modifiable} rows={3} className="champ resize-none" />
        <p className="text-[11.5px] text-[var(--text-muted)] mt-1">
          Ce qui mérite d'être noté sur le déroulement (facultatif).
        </p>
      </div>

      <div className="flex gap-2 flex-wrap pt-2 border-t border-[var(--bordure)]">
        {modifiable && (
          <>
            <button type="button" className="act act-contour"
                    disabled={enCours === 'enregistre'} aria-busy={enCours === 'enregistre' || undefined}
                    onClick={() => agir('enregistre',
                      () => adminApi.majMaFiche(fiche.id, form)).then((r) => r && annoncerSucces('Fiche enregistrée.'))}>
              {enCours === 'enregistre'
                ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Enregistrer
            </button>
            <button type="button" className="act" disabled={enCours === 'valide'}
                    aria-busy={enCours === 'valide' || undefined}
                    onClick={async () => {
                      const enregistre = await agir('valide', async () => {
                        await adminApi.majMaFiche(fiche.id, form);
                        return adminApi.validerMaFiche(fiche.id);
                      });
                      if (enregistre) annoncerSucces('Fiche validée. Signez-la, puis transmettez-la.');
                    }}>
              {enCours === 'valide'
                ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Valider
            </button>
          </>
        )}

        {fiche.numero && (
          <a href={adminApi.urlPdfMaFiche(fiche.id)} target="_blank" rel="noopener noreferrer"
             className="act act-tertiaire">
            <Download className="w-4 h-4" />
            Voir le PDF
          </a>
        )}

        {fiche.statut === 'a_signer' && (
          <>
            {/* Deux circuits, et on ne les mélange pas : signature
                électronique OU scan signé. Le serveur refuse le mélange,
                l'écran propose donc l'un ou l'autre selon ce qui est
                déjà engagé. */}
            {!fiche.a_un_scan && !fiche.signe_par_moi && (
              <BoutonSigner
                genre="fiches" document={fiche} bloc="intervenant"
                libelleBloc="Votre signature, en tant qu'intervenant"
                signatureEnregistree={signaturePrete}
                onSigne={(maj) => onChange(maj)}
              />
            )}
            {!fiche.signe_par_moi && (
              <label className="act act-contour cursor-pointer" aria-busy={enCours === 'scan' || undefined}>
                {enCours === 'scan'
                  ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {enCours === 'scan' ? 'Envoi du scan…'
                  : fiche.a_un_scan ? 'Remplacer le scan signé' : 'Déposer le scan signé'}
                <input type="file" accept="application/pdf,image/png,image/jpeg"
                       className="hidden" disabled={enCours === 'scan'} onChange={deposerScan} />
              </label>
            )}
            {fiche.a_un_scan && (
              <button type="button" className="act act-tertiaire"
                      disabled={enCours === 'retrait'} aria-busy={enCours === 'retrait' || undefined}
                      onClick={() => agir('retrait',
                        () => adminApi.retirerLeScan('fiches', fiche.id))
                        .then((r) => r && annoncerSucces('Scan retiré : vous pouvez signer '
                                                         + 'électroniquement.'))}>
                Retirer le scan
              </button>
            )}
            <button type="button" className="act" disabled={enCours === 'transmis'}
                    aria-busy={enCours === 'transmis' || undefined}
                    onClick={() => agir('transmis',
                      () => adminApi.transmettreMaFiche(fiche.id))
                      .then((r) => r && annoncerSucces('Fiche transmise au point focal.'))}>
              {enCours === 'transmis'
                ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Transmettre
            </button>
          </>
        )}
      </div>

      {fiche.statut === 'a_signer' && (
        <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
          Signez à l’écran — le document vous est montré, signature incrustée,
          avant d’être envoyé — ou imprimez, signez, numérisez et déposez le
          scan. Puis transmettez. Le délai court depuis la fin de la séance et
          se juge sur votre <strong>première</strong> transmission : une
          correction demandée ensuite ne vous met pas en retard.
        </p>
      )}
    </div>
  );
}

/* ── Mes fiches ───────────────────────────────────────────────────── */

function MesFiches({ signaturePrete }) {
  const [etat, setEtat] = useState(null);
  // L'objet erreur du chargement, pas son texte : son statut choisit l'écran.
  const [erreurChargement, setErreurChargement] = useState(null);
  const [ouverte, setOuverte] = useState(null);
  const [enCours, setEnCours] = useState('');

  const charger = useCallback(async () => {
    try {
      setEtat(await adminApi.mesFiches());
      setErreurChargement(null);
    } catch (e) { setErreurChargement(e); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const ouvrir = async (seanceId) => {
    setEnCours(seanceId);
    try {
      setOuverte(await adminApi.ouvrirMaFiche(seanceId));
      await charger();
    } catch (e) {
      annoncerErreur(e.message || 'La fiche n’a pas pu être établie.');
    } finally { setEnCours(''); }
  };

  const relire = async (id) => {
    try { setOuverte(await adminApi.maFiche(id)); } catch (e) {
      annoncerErreur(e.message || 'La fiche n’a pas pu être ouverte.');
    }
  };

  if (etat === null) {
    return erreurChargement
      ? <EtatErreur erreur={erreurChargement} onReessayer={charger} />
      : <SqueletteTableau colonnes={6} />;
  }

  return (
    <div className="space-y-5">
      <AvisActualisation erreur={erreurChargement} onReessayer={charger} />

      {ouverte && (
        <Fenetre nu largeur="large" onClose={() => { setOuverte(null); charger(); }}>
          <Fiche fiche={ouverte} signaturePrete={signaturePrete}
                 onFerme={() => { setOuverte(null); charger(); }}
                 onChange={(maj) => { setOuverte(maj); charger(); }} />
        </Fenetre>
      )}

      {etat.a_etablir?.length > 0 && (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte p-5">
          <h3 className="titre-carte">Séances en attente de fiche</h3>
          <p className="text-[12.5px] text-[var(--text-muted)] mt-1 mb-3">
            Une fiche décrit ce qui a eu lieu : elle s'ouvre une fois la séance
            réalisée, pré-remplie de votre ordre du jour et de votre compte rendu.
          </p>
          <ul className="space-y-2">
            {etat.a_etablir.map((s) => (
              <li key={s.seance}
                  className="flex items-center justify-between gap-3 flex-wrap
                             py-2 border-b border-[var(--bordure)] last:border-0">
                <span className="text-[13.5px]">
                  <strong>{s.organisation}</strong>
                  <span className="text-[var(--text-muted)]"> — {s.objet}, {jour(s.date_debut)}</span>
                </span>
                <button type="button" className="act act-contour"
                        disabled={enCours === s.seance} aria-busy={enCours === s.seance || undefined}
                        onClick={() => ouvrir(s.seance)}>
                  {enCours === s.seance
                    ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
                  Établir la fiche
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {etat.fiches?.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide
            icone={ClipboardList}
            titre="Aucune fiche de suivi"
            aide="Vos fiches apparaîtront ici dès qu'une séance sera marquée réalisée."
          />
        </div>
      ) : (
        <div className="carte-tableau">
          <div className="tableau-hote">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th scope="col">Référence</th>
                  <th scope="col">Organisation</th>
                  <th scope="col">Séance</th>
                  <th scope="col">État</th>
                  <th scope="col">Facturable</th>
                  <th scope="col" className="text-right">Ouvrir</th>
                </tr>
              </thead>
              <tbody>
                {etat.fiches.map((f) => (
                  <tr key={f.id}>
                    <th scope="row" className="font-normal">
                      <span className="text-[13.5px] font-medium text-[var(--text-bold)]">
                        {f.numero || 'Brouillon'}
                      </span>
                      {f.en_retard && (
                        <span className="block text-[11.5px]" style={{ color: 'var(--alerte)' }}>
                          transmise hors délai
                        </span>
                      )}
                    </th>
                    <td className="text-[13px]">{f.organisation}</td>
                    <td className="text-[13px] text-[var(--text-muted)]">{jour(f.date_seance)}</td>
                    <td><Etiquette etat={f.statut} /></td>
                    <td className="text-[12.5px]">
                      {f.facturable
                        ? <span style={{ color: 'var(--succes)' }}>Oui</span>
                        : <span className="text-[var(--text-muted)]">
                            {f.statut === 'recue' ? 'Déjà facturée' : 'Pas encore reçue'}
                          </span>}
                    </td>
                    <td className="text-right">
                      <button type="button" className="act act-tertiaire"
                              onClick={() => relire(f.id)}>
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Mes relevés ──────────────────────────────────────────────────── */

function moisCourant() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function MesReleves({ signaturePrete }) {
  const [etat, setEtat] = useState(null);
  const [preparation, setPreparation] = useState(null);
  const [mois, setMois] = useState(moisCourant());
  const [erreurChargement, setErreurChargement] = useState(null);
  const [refus, setRefus] = useState('');
  const [enCours, setEnCours] = useState('');
  // Les séances cochées : l'intervenant choisit ce qu'il facture. Toutes
  // le sont au départ ; ce qui reste pourra l'être dans un autre relevé.
  const [choix, setChoix] = useState([]);

  const charger = useCallback(async () => {
    try {
      setEtat(await adminApi.mesReleves());
      setErreurChargement(null);
    } catch (e) { setErreurChargement(e); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    let vivant = true;
    setPreparation(null); setRefus('');
    adminApi.preparationReleve(mois)
      .then((d) => { if (vivant) { setPreparation(d); setChoix((d?.lignes || []).map((l) => l.id)); } })
      // Un 400 ici n'est pas une panne : c'est le serveur qui explique
      // pourquoi ce mois ne se facture pas. On montre sa phrase.
      .catch((e) => { if (vivant) setRefus(e.message); });
    return () => { vivant = false; };
  }, [mois]);

  const emettre = async () => {
    setEnCours('emission');
    try {
      await adminApi.emettreReleve(mois, choix);
      annoncerSucces('Relevé émis. Signez-le, puis transmettez-le.');
      await charger();
      const d = await adminApi.preparationReleve(mois).catch(() => null);
      setPreparation(d);
      setChoix((d?.lignes || []).map((l) => l.id));
    } catch (e) {
      annoncerErreur(e.message || 'Le relevé n’a pas pu être émis.');
    } finally { setEnCours(''); }
  };

  const agir = async (releve, action) => {
    setEnCours(releve.id);
    try {
      if (action === 'transmettre') {
        await adminApi.transmettreMonReleve(releve.id);
        annoncerSucces(`Relevé ${releve.numero} transmis pour validation.`);
      } else if (action === 'retirer') {
        await adminApi.retirerLeScan('releves', releve.id);
        annoncerSucces('Scan retiré : vous pouvez signer électroniquement.');
      } else {
        const motif = await demanderMotif({
          titre: `Annuler le relevé ${releve.numero} ?`,
          message: 'Ses fiches seront libérées et pourront figurer sur un autre relevé.',
          confirmer: 'Annuler le relevé', annuler: 'Garder', obligatoire: false,
        });
        if (motif === null) { setEnCours(''); return; }
        await adminApi.annulerMonReleve(releve.id, motif);
        annoncerSucces(`Relevé ${releve.numero} annulé : ses fiches sont libérées.`);
      }
      await charger();
    } catch (e) {
      annoncerErreur(e.message || 'L’opération n’a pas abouti.');
    } finally { setEnCours(''); }
  };

  const deposerScan = async (releve, evenement) => {
    const fichier = evenement.target.files?.[0];
    if (!fichier) return;
    evenement.target.value = '';
    const probleme = validerFichier(fichier, { extensions: FORMATS_SCAN });
    if (probleme) { annoncerErreur(probleme); return; }
    const corps = new FormData();
    corps.append('fichier', fichier);
    setEnCours(releve.id);
    try {
      await adminApi.deposerScanReleve(releve.id, corps);
      annoncerSucces('Scan signé déposé.');
      await charger();
    } catch (e) {
      annoncerErreur(e.message || 'Le scan n’a pas pu être déposé.');
    } finally { setEnCours(''); }
  };

  if (etat === null) {
    return erreurChargement
      ? <EtatErreur erreur={erreurChargement} onReessayer={charger} />
      : <SqueletteTableau colonnes={6} />;
  }

  // Un bénévole ne facture pas, et il doit lire pourquoi : une facture
  // émise sous convention de bénévolat ferait requalifier l'engagement.
  if (!etat.peut_emettre) {
    return (
      <div className="encart encart-info">
        <p>
          <strong>Vous n'émettez pas de relevé de prestation.</strong> Votre
          engagement est une convention de bénévolat : vos frais réels sont
          remboursés hors plateforme. Si un contrat de prestation a été signé,
          demandez à l'administration de le déclarer sur votre compte.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AvisActualisation erreur={erreurChargement} onReessayer={charger} />

      <div className="rounded-carte bg-[var(--bg-card)] shadow-carte p-5 space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-1.5" htmlFor="mois">
              Mois à facturer
            </label>
            <input id="mois" type="month" value={mois}
                   onChange={(e) => setMois(e.target.value)} className="champ" />
          </div>
          <a href={adminApi.urlApercuMonReleve(mois)} target="_blank" rel="noopener noreferrer"
             className="act act-tertiaire">
            <FileText className="w-4 h-4" />
            Aperçu
          </a>
        </div>

        {preparation?.en_attente?.length > 0 && (
          <div className="encart encart-attention">
            <p className="font-semibold mb-1">Séances réalisées pas encore facturables</p>
            <ul className="text-[12.5px] space-y-0.5">
              {preparation.en_attente.map((a) => (
                <li key={a.seance}>{jour(a.date)} · {a.organisation} — {a.raison}</li>
              ))}
            </ul>
          </div>
        )}

        {refus ? (
          <div className="encart encart-info">{refus}</div>
        ) : preparation === null ? (
          <SqueletteTexte lignes={3} />
        ) : preparation.lignes.length === 0 ? (
          <p className="text-[13px] text-[var(--text-muted)]">
            Aucune prestation facturable sur ce mois. Un relevé ne porte que des
            fiches <strong>reçues</strong>, pour des séances où le bénéficiaire
            était présent.
          </p>
        ) : (
          <>
            <div className="tableau-hote">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr>
                    <th scope="col" className="w-8">
                      <input type="checkbox" aria-label="Tout cocher"
                             checked={choix.length === preparation.lignes.length}
                             onChange={(e) => setChoix(e.target.checked ? preparation.lignes.map((l) => l.id) : [])} />
                    </th>
                    <th scope="col">Date</th>
                    <th scope="col">Bénéficiaire</th>
                    <th scope="col">Prestation</th>
                    <th scope="col" className="text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {preparation.lignes.map((l) => (
                    <tr key={l.fiche}>
                      <td>
                        <input type="checkbox" aria-label={`Facturer la séance du ${jour(l.date)}`}
                               checked={choix.includes(l.id)}
                               onChange={() => setChoix((c) => (c.includes(l.id) ? c.filter((x) => x !== l.id) : [...c, l.id]))} />
                      </td>
                      <td className="text-[13px]">
                        {jour(l.date)}
                        <span className="block text-[11px] text-[var(--text-muted)]">{l.fiche}</span>
                      </td>
                      <td className="text-[13px]">{l.organisation}</td>
                      <td className="text-[13px]">{l.type}</td>
                      <td className="text-right text-[13px]">{francs(l.montant)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(() => {
              /* Les totaux de la sélection, à titre indicatif : le serveur
                 les recalcule au barème à l'émission. */
              const brut = preparation.lignes.filter((l) => choix.includes(l.id))
                .reduce((t, l) => t + Number(l.montant || 0), 0);
              const retenue = Math.round(brut * Number(preparation.taux_retenue || 0)) / 100;
              return (
                <div className="flex items-end justify-between gap-4 flex-wrap">
                  <div className="text-[13px] space-y-0.5">
                    <div>{choix.length} séance{choix.length > 1 ? 's' : ''} choisie{choix.length > 1 ? 's' : ''} · Brut : <strong>{francs(brut)}</strong></div>
                    <div className="text-[var(--text-muted)]">
                      Retenue à la source
                      {preparation.taux_non_fixe
                        ? ' — taux non encore fixé par l’administration'
                        : ` (${pourcent(preparation.taux_retenue)} %)`} : −{francs(retenue)}
                    </div>
                    <div className="text-[15px]">
                      Net à payer : <strong>{francs(brut - retenue)}</strong>
                    </div>
                  </div>
                  <button type="button" className="act" disabled={enCours === 'emission' || choix.length === 0}
                          aria-busy={enCours === 'emission' || undefined} onClick={emettre}>
                    {enCours === 'emission'
                      ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
                    Soumettre ma facture
                  </button>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {etat.releves.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide icone={Wallet} titre="Aucun relevé émis"
                    aide="Un relevé couvre un mois, et ne porte que des fiches reçues." />
        </div>
      ) : (
        <div className="carte-tableau">
          <div className="tableau-hote">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th scope="col">Référence</th>
                  <th scope="col">Mois</th>
                  <th scope="col">Prestations</th>
                  <th scope="col" className="text-right">Net</th>
                  <th scope="col">État</th>
                  <th scope="col" className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {etat.releves.map((r) => (
                  <tr key={r.id}>
                    <th scope="row" className="font-normal text-[13.5px] font-medium">
                      {r.numero}
                      {r.motif_correction && (
                        <span className="block text-[11.5px]" style={{ color: 'var(--alerte)' }}>
                          {r.motif_correction}
                        </span>
                      )}
                    </th>
                    <td className="text-[13px] text-[var(--text-muted)]">{jour(r.mois)}</td>
                    <td className="text-[13px]">{r.prestations}</td>
                    <td className="text-right text-[13.5px] font-semibold">
                      {francs(r.montant_net)}
                    </td>
                    <td><Etiquette etat={r.statut} /></td>
                    <td className="text-right whitespace-nowrap">
                      <a href={adminApi.urlPdfMonReleve(r.id)} target="_blank"
                         rel="noopener noreferrer" className="act act-tertiaire" title="PDF">
                        <Download className="w-4 h-4" />
                      </a>
                      {r.statut === 'emis' && (
                        <>
                          {/* Signature électronique OU scan : le serveur
                              refuse le mélange, l'écran ne propose donc
                              que le geste encore ouvert. */}
                          {!r.a_un_scan && !r.signe_par_moi && (
                            <span className="inline-block ml-1">
                              <BoutonSigner
                                genre="releves" document={r} bloc="prestataire"
                                libelleBloc="Votre signature, en tant que prestataire"
                                signatureEnregistree={signaturePrete}
                                onSigne={charger}
                                className="act act-contour" />
                            </span>
                          )}
                          {!r.signe_par_moi && (
                            <label className="act act-tertiaire cursor-pointer ml-1"
                                   title="Scan signé" aria-busy={enCours === r.id || undefined}>
                              {enCours === r.id
                                ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                              <input type="file" accept="application/pdf,image/png,image/jpeg"
                                     className="hidden" disabled={enCours === r.id}
                                     onChange={(e) => deposerScan(r, e)} />
                            </label>
                          )}
                          {r.a_un_scan && (
                            <button type="button" className="act act-tertiaire ml-1"
                                    disabled={enCours === r.id}
                                    onClick={() => agir(r, 'retirer')}
                                    title="Retirer le scan pour signer à l'écran">
                              Retirer le scan
                            </button>
                          )}
                          <button type="button" className="act act-contour ml-1"
                                  disabled={enCours === r.id} aria-busy={enCours === r.id || undefined}
                                  onClick={() => agir(r, 'transmettre')}>
                            <Send className="w-4 h-4" />
                            Transmettre
                          </button>
                          <button type="button" className="act act-tertiaire ml-1"
                                  disabled={enCours === r.id}
                                  onClick={() => agir(r, 'annuler')} title="Annuler">
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
        Annuler un relevé <strong>libère ses fiches</strong> : elles repassent
        sur le relevé suivant. Un relevé payé ne s'annule plus.
      </p>
    </div>
  );
}

/* ── Mes évaluations ──────────────────────────────────────────────── */

const CRITERES = [
  ['volume', "Volume d'intervention", 'note_volume'],
  ['assiduite', 'Assiduité et ponctualité', 'note_assiduite'],
  ['livrables', 'Qualité des livrables', 'note_livrables'],
  ['satisfaction', 'Satisfaction des bénéficiaires', 'note_satisfaction'],
  ['ethique', 'Respect du cadre éthique', 'note_ethique'],
];

function MesEvaluations() {
  const [grilles, setGrilles] = useState(null);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(() => {
    setErreur(null);
    adminApi.mesEvaluations()
      .then((d) => setGrilles(d.evaluations || []))
      .catch((e) => setErreur(e));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  if (grilles === null) {
    return erreur
      ? <EtatErreur erreur={erreur} onReessayer={charger} />
      : <div className="space-y-5"><SqueletteCarte /><SqueletteCarte /></div>;
  }

  return (
    <div className="space-y-5">

      {grilles.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide
            icone={Award}
            titre="Aucune évaluation finalisée"
            aide="Les grilles trimestrielles apparaissent ici une fois rendues. Une grille en cours d'arbitrage ne s'affiche pas."
          />
        </div>
      ) : grilles.map((g) => (
        <div key={g.id} className="rounded-carte bg-[var(--bg-card)] shadow-carte p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="titre-carte">T{g.trimestre} {g.annee} — {g.score} / 25</h3>
              <p className="text-[12.5px] text-[var(--text-muted)] mt-1">
                {g.numero} · évaluée le {jour(g.finalisee_le)}
                {g.evaluateur ? ` par ${g.evaluateur}` : ''}
              </p>
            </div>
            <a href={adminApi.urlPdfMonEvaluation(g.id)} target="_blank"
               rel="noopener noreferrer" className="act act-contour">
              <Download className="w-4 h-4" />
              PDF
            </a>
          </div>

          <div className="tableau-hote mt-4">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th scope="col">Critère</th>
                  <th scope="col">Mesuré sur la plateforme</th>
                  <th scope="col" className="text-right">Note</th>
                </tr>
              </thead>
              <tbody>
                {CRITERES.map(([cle, libelle, champ]) => (
                  <tr key={cle}>
                    <th scope="row" className="font-normal text-[13px]">{libelle}</th>
                    <td className="text-[12.5px] text-[var(--text-muted)]">
                      {g.mesures?.[cle]?.mesure || '—'}
                      <span className="block text-[11px]">
                        Seuil : {g.mesures?.[cle]?.seuil || '—'}
                      </span>
                    </td>
                    <td className="text-right text-[13.5px] font-semibold">
                      {g.notes?.[champ] ?? '—'}/5
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {g.commentaire_general && (
            <p className="text-[13px] mt-3">{g.commentaire_general}</p>
          )}
          {g.plan_amelioration && (
            <div className="encart encart-alerte mt-3">
              <strong>Plan d'amélioration :</strong> {g.plan_amelioration}
            </div>
          )}
          <p className="text-[11.5px] text-[var(--text-muted)] mt-3">
            Les mesures sont relevées par la plateforme ; les notes sont
            attribuées par l'évaluateur, qui reste seul juge.
          </p>
        </div>
      ))}
    </div>
  );
}

/* ── L'assemblage ─────────────────────────────────────────────────── */

export default function MesDocuments() {
  const [vue, setVue] = useState('fiches');
  /* Lu une fois pour tout l'écran : proposer « Signer » à quelqu'un qui
     n'a pas enregistré sa signature, c'est l'envoyer dans un refus. On
     l'oriente vers son profil à la place. */
  const [signaturePrete, setSignaturePrete] = useState(false);

  useEffect(() => {
    let vivant = true;
    adminApi.maSignature()
      .then((d) => { if (vivant) setSignaturePrete(!!d.enregistree); })
      .catch(() => { if (vivant) setSignaturePrete(false); });
    return () => { vivant = false; };
  }, []);

  const vues = [
    { cle: 'fiches', libelle: 'Mes fiches de suivi', Icone: ClipboardList },
    { cle: 'releves', libelle: 'Mes relevés', Icone: Wallet },
    // Le relevé dit ce qui est dû ; le retrait, ce qu'on en demande. Les
    // mettre sur le même écran laisserait croire qu'émettre un relevé
    // déclenche un versement.
    { cle: 'retraits', libelle: 'Mes retraits', Icone: Banknote },
    { cle: 'evaluations', libelle: 'Mes évaluations', Icone: Award },
  ];

  return (
    <div className="space-y-5">
      <div className="flex gap-1 flex-wrap border-b border-[var(--border)] pb-px">
        {vues.map(({ cle, libelle, Icone }) => {
          const actif = vue === cle;
          return (
            <button key={cle} type="button" onClick={() => setVue(cle)}
                    style={{ minHeight: 44,
                             color: actif ? 'var(--text-bold)' : 'var(--text-muted)',
                             boxShadow: actif ? 'inset 0 -2px 0 var(--accent)' : 'none' }}
                    className="act act-tertiaire">
              <Icone className="w-4 h-4" aria-hidden="true" />
              {libelle}
            </button>
          );
        })}
      </div>

      {vue === 'fiches' ? <MesFiches signaturePrete={signaturePrete} />
        : vue === 'releves' ? <MesReleves signaturePrete={signaturePrete} />
          : vue === 'retraits' ? <MesRetraits />
            : <MesEvaluations />}
    </div>
  );
}
